import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusBadge from '../../installation-tracking/components/StatusBadge';




const BrandingItemsTable = ({ items, onEdit, onDelete, onDuplicate, onStatusChange }) => {
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'planned', label: 'Planned' },
    { value: 'installed', label: 'Installed' },
    { value: 'verified', label: 'Verified' },
    { value: 'expired', label: 'Expired' }
  ];

  const statusConfig = {
    planned: { color: 'bg-warning/10 text-warning border-warning/20', icon: 'Clock' },
    installed: { color: 'bg-primary/10 text-primary border-primary/20', icon: 'CheckCircle' },
    verified: { color: 'bg-success/10 text-success border-success/20', icon: 'Shield' },
    expired: { color: 'bg-error/10 text-error border-error/20', icon: 'AlertTriangle' }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })?.format(new Date(date));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedItems = items?.filter(item => {
      const matchesStatus = !filterStatus || item?.status === filterStatus;
      const matchesSearch = !searchQuery || 
        item?.brandName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.storeDetails?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        item?.rateTypeDetails?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      return matchesStatus && matchesSearch;
    })?.sort((a, b) => {
      let aValue = a?.[sortField];
      let bValue = b?.[sortField];
      
      if (sortField === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev?.includes(itemId) 
        ? prev?.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems?.length === filteredAndSortedItems?.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredAndSortedItems?.map(item => item?.id));
    }
  };

  const totalFees = filteredAndSortedItems?.reduce((sum, item) => sum + item?.totalFees, 0);

  if (items?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Branding Items Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Start by adding your first branding item using the form above
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Branding Items</h3>
        <p className="text-sm text-muted-foreground">Manage branding items with unique IDs</p>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">Branding ID</th>
              <th className="text-left p-4 font-medium text-foreground">Store</th>
              <th className="text-left p-4 font-medium text-foreground">Brand</th>
              <th className="text-left p-4 font-medium text-foreground">Branding Medium</th>
              <th className="text-left p-4 font-medium text-foreground">KV ID</th>
              <th className="text-left p-4 font-medium text-foreground">Area</th>
              <th className="text-left p-4 font-medium text-foreground">Cost</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="w-32 p-4"></th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedItems?.map((item) => (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="font-mono text-sm font-medium text-primary">{item?.brandingId}</div>
                  <div className="text-xs text-muted-foreground">{item?.id}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{item?.storeDetails?.label?.split(' - ')?.[0]}</div>
                  <div className="text-sm text-muted-foreground">{item?.storeDetails?.locationCode}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground capitalize">{item?.brandName?.replace('-', ' ')}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{item?.mediumDetails?.label}</div>
                  <div className="text-sm text-muted-foreground">{item?.mediumDetails?.code}</div>
                </td>
                <td className="p-4">
                  <div className="font-mono text-sm text-foreground">{item?.kvId}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{item?.area} sqft</div>
                </td>
                <td className="p-4">
                  <div className="font-medium text-foreground">{formatCurrency(item?.totalCost)}</div>
                  <div className="text-xs text-muted-foreground">Rate: {formatCurrency(item?.unitRate)}</div>
                </td>
                <td className="p-4">
                  <StatusBadge status={item?.status} />
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(item)}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit2" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(item)}
                      className="h-8 w-8"
                    >
                      <Icon name="Copy" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete(item?.id)}
                      className="h-8 w-8 text-error hover:text-error"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden">
        {filteredAndSortedItems?.map((item) => (
          <div key={item?.id} className="p-4 border-b border-border last:border-b-0">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-sm font-bold text-primary">{item?.brandingId}</div>
                <div className="text-sm text-muted-foreground">{item?.storeDetails?.label?.split(' - ')?.[0]}</div>
              </div>
              <StatusBadge status={item?.status} size="sm" />
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <div className="text-sm text-muted-foreground">Brand</div>
                <div className="font-medium text-foreground capitalize">{item?.brandName?.replace('-', ' ')}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Branding Medium</div>
                <div className="font-medium text-foreground">{item?.mediumDetails?.label}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">KV ID</div>
                <div className="font-mono text-sm text-foreground">{item?.kvId}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Area</div>
                <div className="font-medium text-foreground">{item?.area} sqft</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">{formatCurrency(item?.totalCost)}</div>
                <div className="text-xs text-muted-foreground">Rate: {formatCurrency(item?.unitRate)}</div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(item)}
                  iconName="Edit2"
                  iconPosition="left"
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDuplicate(item)}
                  iconName="Copy"
                  iconPosition="left"
                >
                  Duplicate
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BrandingItemsTable;